import { HelpIcon } from '@daily/shared/components/Icons/HelpIcon';
import { useSnackbar } from '@daily/shared/contexts/Snackbar';
import { DailyEventObjectMeetingSessionStateUpdated } from '@daily-co/daily-js';
import {
  ExtendedDailyParticipant,
  useDaily,
  useLocalSessionId,
  useParticipantIds,
} from '@daily-co/daily-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MAIN_ROOM_IDENTIFIER } from '/components/BreakoutRooms/helpers/constants';
import {
  BreakoutRoom,
  BreakoutsSessionData,
  BreakoutsUserData,
} from '/contexts/BreakoutsProvider';
import { useUserData } from '/hooks/useUserData';
import { getPrebuiltUserData, setPrebuiltUserData } from '/lib/userData';

import { useMeetingSessionData } from './useMeetingSessionData';

interface UseBreakoutSessionStateArgs {
  onMeetingSessionStateUpdated?(
    ev: DailyEventObjectMeetingSessionStateUpdated
  ): void;
}

/**
 * Returns all derived state from meeting session and user data.
 */
export const useBreakoutSessionState = ({
  onMeetingSessionStateUpdated,
}: UseBreakoutSessionStateArgs = {}) => {
  const { t } = useTranslation();
  const daily = useDaily();
  const localSessionId = useLocalSessionId();
  const { addMessage } = useSnackbar();

  const { sessionData } = useMeetingSessionData<BreakoutsSessionData>({
    onMeetingSessionStateUpdated,
  });

  const breakoutStatus = sessionData?.br?.status;
  const breakoutHost = sessionData?.br?.host;
  const breakoutMapping = useMemo(
    () => sessionData?.br?.mapping ?? {},
    [sessionData?.br?.mapping]
  );
  const endDate = useMemo(() => {
    if (!sessionData?.br?.config?.endDate) return null;
    return new Date(sessionData?.br?.config?.endDate);
  }, [sessionData?.br?.config?.endDate]);

  const localUserData = useUserData<BreakoutsUserData>([localSessionId]);
  const currentRoomId = localUserData[localSessionId]?.br?.currentRoomId;
  const joinedState = localUserData[localSessionId]?.br?.status;

  const hasJoinedBreakout =
    currentRoomId && sessionData?.br?.status === 'ongoing';

  const currentBreakoutRoom: BreakoutRoom = {
    id: currentRoomId ?? MAIN_ROOM_IDENTIFIER,
    name: breakoutMapping?.[currentRoomId]?.name,
    users: breakoutMapping?.[currentRoomId]?.users,
  };

  const invited = Object.entries(breakoutMapping).find(([, room]) =>
    room.users.includes(localSessionId)
  );
  const invitedBreakoutRoom: BreakoutRoom = invited
    ? {
        id: invited?.[0],
        name: invited?.[1]?.name,
        users: invited?.[1]?.users,
      }
    : null;

  const allIds = useParticipantIds();
  const breakoutUserDatas = useUserData<BreakoutsUserData>(
    allIds,
    useCallback((_id, data) => Boolean(data?.br?.currentRoomId), [])
  );
  const anyBreakoutParticipantIds = Object.keys(breakoutUserDatas);
  const mainRoomParticipantIds = allIds.filter(
    (id) => !anyBreakoutParticipantIds.includes(id)
  );
  const userDatasAsArray = Object.entries(breakoutUserDatas);

  const breakoutParticipantIds = userDatasAsArray
    .filter(
      ([, data]) => currentRoomId && data?.br?.currentRoomId === currentRoomId
    )
    .map(([id]) => id);

  const invitedUserDatas = useUserData<BreakoutsUserData>(
    allIds,
    useCallback((_id, data) => data?.br?.status === 'invited', [])
  );
  const invitedParticipants = Object.keys(invitedUserDatas);

  const breakoutMappingAsArray = Object.entries(breakoutMapping);
  const breakoutMappingValues = Object.values(breakoutMapping);

  /* Find all users in a room by its name*/
  const breakoutParticipantsByRoomName = useCallback(
    (roomName) => {
      // get room id by its name
      const roomId = breakoutMappingAsArray.find(
        ([, room]) => room.name === roomName
      )?.[0];

      // get the actual location of participants by room name. for example:
      // user A is in room 1, and has been invited to room 2. their location remains
      // room 1 _until_ they accept the invite.
      return breakoutMappingValues
        .filter((m) => m.name === roomName)
        .map(() => {
          return userDatasAsArray
            .filter(([, userData]) => userData.br.currentRoomId === roomId)
            .map(([userId]) => userId);
        });
    },
    [breakoutMappingAsArray, breakoutMappingValues, userDatasAsArray]
  );

  /* Find all users in a room by its id*/
  const breakoutParticipantsByRoomId = useCallback(
    (roomId: string) => {
      return breakoutMapping[roomId]?.users;
    },
    [breakoutMapping]
  );

  const participantsAskingForHelp = useParticipantIds({
    filter: useCallback(
      (p: ExtendedDailyParticipant) => {
        const userData: BreakoutsUserData = getPrebuiltUserData(
          daily,
          p.local ? 'local' : p.session_id
        );
        return userData?.br?.help && !p.local;
      },
      [daily]
    ),
  });

  const userData = useUserData<BreakoutsUserData>([localSessionId]);
  const isRequestingHelp = Boolean(userData?.[localSessionId]?.br?.help);
  const updateAskForHelpStatus = useCallback(
    (needHelp: boolean) => {
      setPrebuiltUserData(daily, {
        ...(userData?.[localSessionId] ?? {}),
        br: {
          ...(userData?.[localSessionId]?.br ?? {}),
          help: needHelp,
        },
      });
      addMessage({
        content: needHelp
          ? t('breakouts.hostReceivedYourRequest')
          : t('breakouts.requestWasCanceled'),
        icon: <HelpIcon size={16} />,
        type: 'info',
      });
    },
    [addMessage, daily, localSessionId, t, userData]
  );

  return {
    breakoutHost,
    breakoutParticipantsByRoomId,
    breakoutMapping,
    breakoutParticipantsByRoomName,
    breakoutParticipantIds,
    breakoutStatus,
    currentBreakoutRoom,
    endDate,
    hasJoinedBreakout,
    invitedBreakoutRoom,
    invitedParticipants,
    joinedState,
    mainRoomParticipantIds,
    participantsAskingForHelp,
    isRequestingHelp,
    updateAskForHelpStatus,
  };
};
